<template>
  <p class="zoom" @mousedown="zoomStart" ref="refZoomContainer">
    <img :src="src" :alt="alt" ref="refZoomImage">
  </p>
</template>
    
<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';

const x = ref(0) // 画像を選択したx位置
const y = ref(0) // 画像を選択したy位置
const initialX = ref(0) // ズーム開始時のカーソルX座標
const initialY = ref(0) // ズーム開始時のカーソルY座標
const startX = ref(0) // スワイプ開始時のカーソルX座標
const startY = ref(0) // スワイプ開始時のカーソルY座標
const currentX = ref(0) // スワイプ後のカーソルX座標
const currentY = ref(0) // スワイプ後のカーソルY座標
const imageScaleFactor = ref(3) // 画像を拡大する倍数
const refZoomContainer = ref(null) // ズームコンテナの参照
const refZoomImage = ref(null) // ズーム画像の参照
const isZoomed = ref(false) // ズーム状態のフラグ

const props = withDefaults(
  defineProps<{
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    imgClass?: string;
  }>(),
  {
    src: "",
    alt: "",
    width: "",
    height: "",
    imgClass: "",
  }
)
onMounted(() => {
  // 初回の表示時の処理
  setContainerAttributesAndStyles()
})

watch(() => props, () => {
  // propsが変更されたときに実行される処理
  setContainerAttributesAndStyles()
}, { deep: true })

// コンテナの属性とスタイルを設定
const setContainerAttributesAndStyles = () => {
  nextTick(() => {
    if (props.imgClass) {
      // imgClassを空白で分割し、各クラス名を配列にする
      const classes = props.imgClass.split(" ");
      // 各クラス名について、コンテナにクラスを追加する
      classes.forEach(className => {
        // classNameが空でない場合のみclassListに追加する
        if (className.trim() !== "") {
          refZoomContainer.value.classList.add(className);
        }
      });
    }
    refZoomContainer.value.setAttribute('alt', props.alt);
    refZoomContainer.value.style.width = props.width
    refZoomContainer.value.style.height = props.height
  })
}

// ズーム機能を開始するためのイベントハンドラ
function zoomStart(e) {
  // もしimgClassに"nozoom"が含まれている場合、returnして拡大しない。
  if(props.imgClass.includes("nozoom")){
    return
  }
  // zoomContainerとzoomImageが存在するか確認
  if (refZoomContainer.value && refZoomImage.value) {
    // 初期の背景サイズをズームレベルに基づいて設定
    refZoomContainer.value.style.backgroundImage = `url(${props.src})`
    refZoomContainer.value.style.backgroundSize = (refZoomImage.value.clientWidth * imageScaleFactor.value) + "px " + (refZoomImage.value.clientHeight * imageScaleFactor.value) + "px";
    refZoomContainer.value.style.backgroundRepeat = "no-repeat"
    refZoomContainer.value.style.cursor = "grab";
    refZoomImage.value.style.transform = "translateZ(0)";
    refZoomImage.value.style.opacity = "0";
    // 初期のカーソル位置を取得
    getPosition(e)
    // ズームされていない場合
    if (!isZoomed.value) {
      // 一番最初タッチしたときのカーソル位置をinitial変数に保存
      initialX.value = x.value
      initialY.value = y.value
      // カーソルの初期位置に基づいて背景位置を設定
      refZoomContainer.value.style.backgroundPosition = x.value + '% ' + y.value + '%';
      isZoomed.value = true
    }
    // マウスおよびタッチのイベントリスナーを追加
    refZoomContainer.value.addEventListener('touchstart', touchSwipeStart);
    refZoomContainer.value.addEventListener('mousemove', zoomMove);
    refZoomContainer.value.addEventListener('touchmove', zoomMove);
    refZoomContainer.value.addEventListener('mouseleave', zoomLeave);
    document.addEventListener("touchend", zoomLeave);
  }
}
// スワイプ操作の開始時に呼び出される関数
function touchSwipeStart(e) {
  // デフォルトのイベントをキャンセル
  e.preventDefault();
  // ズームされている場合
  if (isZoomed.value) {
    getPosition(e)
    // タッチ操作の開始位置を保存
    startX.value = x.value
    startY.value = y.value
  }
}
// ズーム移動を処理するためのイベントハンドラ
function zoomMove(e) {
  // イベントのデフォルト動作を防ぐ
  e.preventDefault();
  // 現在のカーソル位置を取得
  getPosition(e)
  const touches = e.touches
  // タッチデバイスの場合の処理
  if (touches) {
    // カーソルの移動量を計算
    const moveX = (startX.value - x.value);
    const moveY = (startY.value - y.value);
    // 移動後のカーソル位置を計算し、背景位置を0から100%の範囲内に制限して設定
    currentX.value = Math.min(100, Math.max(0, (initialX.value + moveX)));
    currentY.value = Math.min(100, Math.max(0, (initialY.value + moveY)));
    // 背景位置を更新
    refZoomContainer.value.style.backgroundPosition = currentX.value + '% ' + currentY.value + '% ';
  } else {
    // カーソルが有効な範囲内にあるか確認
    if (x.value < 0 || x.value > 100 || y.value < 0 || y.value > 100) {
      zoomLeave(e);
      return;
    }
    // 現在のカーソル位置に基づいて背景位置を更新
    refZoomContainer.value.style.backgroundPosition = x.value + '% ' + y.value + '%';
  }
}
// ズーム機能の終了を処理するためのイベントハンドラ
function zoomLeave(e) {
  // イベントの伝播を停止し、スタイルをリセット
  e.stopPropagation();
  // イベントの発生元要素を取得
  if(refZoomImage.value && refZoomContainer.value){
    const target = e.target as HTMLElement;
    const touches = e.touches
    // タッチデバイスからのイベントの場合
    if (touches) {
      // スワイプ後のカーソル位置を基準になるinitialに設定して更新
      initialX.value = currentX.value
      initialY.value = currentY.value
      // タッチがコンテナ内でない場合、画像を元のサイズに戻す
      if (!refZoomContainer.value.contains(target)) {
        refZoomContainer.value.style.backgroundImage = "none"
        refZoomImage.value.style.opacity = "1";
        isZoomed.value = false
        document.removeEventListener("touchend", zoomLeave);
      }
    } else {
      // マウス操作の場合
      refZoomContainer.value.style.backgroundImage = "none"
      refZoomImage.value.style.opacity = "1";
      refZoomContainer.value.style.cursor = "zoom-in";
      isZoomed.value = false
    }
    // マウスイベントのリスナーを削除
    refZoomContainer.value.removeEventListener('mousemove', zoomMove);
    refZoomContainer.value.removeEventListener('mouseleave', zoomLeave);
    // ズームが解除された場合
    if (!isZoomed.value) {
      // タッチデバイス向けのリスナーも削除
      refZoomContainer.value.removeEventListener('touchstart', touchSwipeStart)
      refZoomContainer.value.removeEventListener('touchmove', zoomMove);
    }
  }
}
// コンテナに対するカーソル位置を取得するヘルパー関数
function getPosition(e) {
  // コンテナの境界矩形を取得
  const rect = refZoomContainer.value.getBoundingClientRect();
  const touches = e.touches
  // イベントのタイプに基づいてカーソルの位置を決定F
  if (touches) {
    x.value = ((touches[0].pageX - rect.left) / refZoomContainer.value.offsetWidth) * 100
    y.value = ((touches[0].pageY - rect.top) / refZoomContainer.value.offsetHeight) * 100
  } else {
    x.value = (e.offsetX / refZoomContainer.value.offsetWidth) * 100;
    y.value = (e.offsetY / refZoomContainer.value.offsetHeight) * 100;
  }
}
onUnmounted(() => {
  // remove event listener to decrease browser memory use
  document.removeEventListener("touchend", zoomLeave)
})
</script>
    